import React from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { 
  UserIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon, 
  CalendarIcon,
  CurrencyDollarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const CustomerDetail = ({ customer }) => {
  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Fetch customer bookings
  const { data: bookingsData, isLoading, isError } = useQuery(
    ['customerBookings', customer.id],
    async () => {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const response = await axios.get(`${apiUrl}/admin/customers/${customer.id}/bookings`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data.data;
    },
    {
      enabled: !!customer.id,
      onError: (error) => {
        console.error('Error fetching customer bookings:', error);
      }
    }
  );

  // Render status badge
  const renderStatusBadge = (status) => {
    // Handle undefined or null status
    if (status === undefined || status === null) {
      return (
        <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
          Unknown
        </span>
      );
    }
    
    // Convert numeric or boolean status to string
    const isActive = status === 'active' || status === 1 || status === true;
    const statusText = isActive ? 'active' : 'inactive';
    const bgColor = isActive ? 'bg-green-100' : 'bg-red-100';
    const textColor = isActive ? 'text-green-800' : 'text-red-800';
    
    return (
      <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${bgColor} ${textColor}`}>
        {statusText}
      </span>
    );
  };

  // Render booking status badge
  const renderBookingStatusBadge = (status) => {
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    
    switch (status.toLowerCase()) {
      case 'confirmed':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'pending':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'cancelled':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      case 'completed':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        break;
      default:
        break;
    }
    
    return (
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${bgColor} ${textColor}`}>
        {status}
      </span>
    );
  };

  // Placeholder bookings data
  const placeholderBookings = [
    {
      id: 1,
      booking_number: 'BK-2023-001',
      tour: {
        id: 1,
        name: 'Maasai Mara Safari',
        location: 'Maasai Mara'
      },
      date: '2023-04-15',
      travelers: 2,
      total_amount: 2400,
      status: 'confirmed',
      created_at: '2023-03-20T10:30:00Z'
    },
    {
      id: 2,
      booking_number: 'BK-2023-002',
      tour: {
        id: 2,
        name: 'Amboseli National Park',
        location: 'Amboseli'
      },
      date: '2023-04-18',
      travelers: 3,
      total_amount: 2850,
      status: 'pending',
      created_at: '2023-03-22T14:45:00Z'
    },
    {
      id: 3,
      booking_number: 'BK-2023-003',
      tour: {
        id: 3,
        name: 'Lake Nakuru Tour',
        location: 'Nakuru'
      },
      date: '2023-04-20',
      travelers: 4,
      total_amount: 3000,
      status: 'cancelled',
      created_at: '2023-03-25T09:15:00Z'
    }
  ];

  // Use placeholder data if API data is not available
  const bookings = bookingsData || placeholderBookings;

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Customer Details</h2>
        {renderStatusBadge(customer.is_active)}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Customer Information */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Personal Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <UserIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Full Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{customer.name}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <EnvelopeIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Email Address</dt>
                  <dd className="mt-1 text-sm text-gray-900">{customer.email}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <PhoneIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Phone Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">{customer.phone_number}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <MapPinIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Address</dt>
                  <dd className="mt-1 text-sm text-gray-900">{customer.address}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <CalendarIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Joined Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(customer.created_at)}</dd>
                </div>
              </div>
            </div>
          </div>

          {/* Account Summary */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Account Summary</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <ClockIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Total Bookings</dt>
                  <dd className="mt-1 text-sm text-gray-900">{customer.bookings_count}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <CurrencyDollarIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Total Spent</dt>
                  <dd className="mt-1 text-sm text-gray-900">${customer.total_spent.toLocaleString()}</dd>
                </div>
              </div>
              {customer.last_booking_date && (
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                      <CalendarIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-gray-500">Last Booking</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatDate(customer.last_booking_date)}</dd>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Customer Bookings */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Recent Bookings</h3>
          
          {isLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-16 bg-gray-200 rounded"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          ) : isError ? (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-red-500">Error loading bookings. Please try again.</p>
            </div>
          ) : bookings.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-gray-500">No bookings found for this customer.</p>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Booking #
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tour
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-primary">
                          {booking.booking_number}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{booking.tour.name}</div>
                          <div className="text-xs text-gray-500">{booking.tour.location}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(booking.date)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          ${booking.total_amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {renderBookingStatusBadge(booking.status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notes */}
      {customer.notes && (
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Notes</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-900">{customer.notes}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetail;
